<template>
  <div class="preview-content" ref="previewContentRef">
    <div v-if="showInitialSpinner" class="text-center w-100 no-preview-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <template v-else>
      <!-- Two iframes swap via opacity so the viewer never sees the blank
           intermediate state of an srcdoc re-navigation. The inactive one
           preloads the next payload in the background and we only flip
           `activeFrame` once its @load fires. -->
      <iframe
        ref="frameARef"
        class="preview-frame"
        :class="{ active: activeFrame === 'a' }"
        name="preview-iframe-a"
        :style="previewStyleA"
        frameborder="0"
        scrolling="no"
        :srcdoc="iFrameSrcDocA || undefined"
        @load="onFrameLoad('a')" />
      <iframe
        ref="frameBRef"
        class="preview-frame"
        :class="{ active: activeFrame === 'b' }"
        name="preview-iframe-b"
        :style="previewStyleB"
        frameborder="0"
        scrolling="no"
        :srcdoc="iFrameSrcDocB || undefined"
        @load="onFrameLoad('b')" />
    </template>
  </div>
  <div class="text-center py-2 border-top" v-if="previews.length > 1">
    <LeadButton
      :disabled="['prev', 'both'].includes(disabledBtns)"
      @click="gotoPreview(-1)"
      variant="white"
      class="me-2">
      <template #icon>
        <IconChevronLeft size="16" />
      </template>
    </LeadButton>
    <span class="text-muted mx-2" style="font-weight: bold">{{ currentPreview?.name || 'Preview' }}</span>
    <LeadButton
      :disabled="['next', 'both'].includes(disabledBtns)"
      @click="gotoPreview(1)"
      variant="white"
      class="ms-2">
      <template #icon>
        <IconChevronRight size="16" class="rotate-180" />
      </template>
    </LeadButton>
  </div>
  <div v-else-if="currentPreview" class="text-center mb-2">
    <small class="text-muted" style="font-weight: bold">{{ currentPreview.name || 'Preview' }}</small>
  </div>
</template>

<script setup lang="ts">
import { request } from '@/api/client';
import type { OrderPreview } from '@/types/types';
import { debounce } from '@prospective/lithium';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';

const props = defineProps<{
  providerId: string | null | undefined;
  providerName?: string | null | undefined;
  orderId?: string | undefined;
  version?: number;
  object?: Record<string, any>;
}>();

const previewContentRef = ref<HTMLElement | null>(null);
const frameARef = ref<HTMLIFrameElement | null>(null);
const frameBRef = ref<HTMLIFrameElement | null>(null);
const currentPreviewIndex = ref(0);
const currentPreviewKey = ref('');
const frameWidth = ref(0);
const iFrameSrcDocA = ref<string | null>(null);
const iFrameSrcDocB = ref<string | null>(null);
const activeFrame = ref<'a' | 'b'>('a');
const pendingFrame = ref<'a' | 'b' | null>(null);
const disabledBtns = ref<'none' | 'prev' | 'next' | 'both'>('both');
// bumped on every navigation so an in-flight fetch that resolves after a newer
// navigation has started can recognize it's stale and discard its own result
let requestSeq = 0;
const previewStyleA = ref<Record<string, string>>({});
const previewStyleB = ref<Record<string, string>>({});
const previews = ref<OrderPreview[]>([]);
const providerStore = useProviderStore();
const { obj } = storeToRefs(providerStore);
const loadingPreview = ref(true);

const hasAnyPreview = computed(() => !!(iFrameSrcDocA.value || iFrameSrcDocB.value));
const showInitialSpinner = computed(() => loadingPreview.value && !hasAnyPreview.value);

/* -------------------------------- COMPUTED -------------------------------- */
const currentPreview = computed((): OrderPreview | null => {
  for (const preview of previews.value) {
    if (preview.key === currentPreviewKey.value) {
      return preview;
    }
  }

  return null;
});

/* ---------------------------------- WATCH --------------------------------- */
watch(
  () => props.object,
  () => {
    previews.value = [];
    if (props.object && props.object?.previews) {
      (Object.values(props.object?.previews) as OrderPreview[]).map((p: OrderPreview) => {
        previews.value.push(p);
      });
    }
    calcPreview();
  },
  { immediate: true, deep: true }
);

watch(
  () => obj.value,
  () => {
    // the available previews depend on the current values — picking another banner adds or removes
    // one — so refresh the list before re-rendering instead of reusing the list from the last save.
    // not immediate: the initial list comes from the campaign object watcher above.
    previewListDebouncer();
  },
  { deep: true }
);

// Reset preview index when provider changes
watch(
  () => props.providerName,
  () => {
    currentPreviewIndex.value = 0;
    iFrameSrcDocA.value = null;
    iFrameSrcDocB.value = null;
    activeFrame.value = 'a';
    pendingFrame.value = null;
    calcPreview();
  }
);

watch(
  () => props.version,
  () => {
    previewDebouncer();
  }
);

/* --------------------------------- METHODS -------------------------------- */
/** Derives which pager arrows are disabled from the current position and list length. */
function updateDisabledBtns() {
  if (previews.value.length < 2) {
    disabledBtns.value = 'both';
  } else if (currentPreviewIndex.value === 0) {
    disabledBtns.value = 'prev';
  } else if (currentPreviewIndex.value === previews.value.length - 1) {
    disabledBtns.value = 'next';
  } else {
    disabledBtns.value = 'none';
  }
}

function gotoPreview(prevOrNext: number) {
  currentPreviewIndex.value = Math.max(0, Math.min(currentPreviewIndex.value + prevOrNext, previews.value.length - 1));
  updateDisabledBtns();

  currentPreviewKey.value = previews.value[currentPreviewIndex.value].key;
  previewDebouncer();
}
function calcPreview() {
  if (previews.value.length > 0) {
    // clamp first, then take the key from that position — deriving the key from index 0 while
    // keeping the old index desyncs the pager, so the next arrow would skip ahead
    currentPreviewIndex.value = Math.max(0, Math.min(currentPreviewIndex.value, previews.value.length - 1));
    currentPreviewKey.value = previews.value[currentPreviewIndex.value].key || '';

    updateDisabledBtns();
  } else {
    currentPreviewKey.value = '';
    currentPreviewIndex.value = 0;
    iFrameSrcDocA.value = null;
    iFrameSrcDocB.value = null;
    activeFrame.value = 'a';
    pendingFrame.value = null;
    disabledBtns.value = 'both';
  }

  nextTick(() => {
    if (previewContentRef.value) {
      frameWidth.value = previewContentRef.value.getBoundingClientRect().width;
      previewDebouncer();
    }
  });
}

function onFrameLoad(key: 'a' | 'b') {
  // Only swap if this is the frame we just populated; ignore the initial
  // empty-srcdoc load fired by Vue when the iframes first mount.
  if (pendingFrame.value !== key) return;
  activeFrame.value = key;
  pendingFrame.value = null;
}

// the preview content (e.g. an expanded "more" caption) can grow after the iframe has
// already loaded; the iframe reports its real height so we can rescale to fit the fixed-size
// panel instead of clipping it.
function onPreviewMessage(event: MessageEvent) {
  if (event.data?.type !== 'pjas-preview-resize') return;

  if (event.source === frameARef.value?.contentWindow) {
    applyFrameStyle('a', event.data.height);
  } else if (event.source === frameBRef.value?.contentWindow) {
    applyFrameStyle('b', event.data.height);
  }
}

function applyFrameStyle(target: 'a' | 'b', heightOverride?: number) {
  const { width, height, scale, boundingClient } = getPreviewScale(heightOverride);
  let marginLeft = 0;
  let marginTop = 0;
  if (boundingClient) {
    marginLeft = boundingClient.width / 2 - (width * scale) / 2;
    marginTop = Math.max(0, boundingClient.height / 2 - (height * scale) / 2);
  }
  const newStyle = {
    transform: `scale(${scale})`,
    transformOrigin: '0 0',
    width: `${width}px`,
    height: `${height}px`,
    marginLeft: `${marginLeft}px`,
    marginTop: `${marginTop}px`,
  };

  if (target === 'a') previewStyleA.value = newStyle;
  else previewStyleB.value = newStyle;
}
/** Re-reads which previews exist for the values currently in the form, then re-renders. */
const previewListDebouncer = debounce(() => {
  if (!props.providerId) {
    calcPreview();
    return;
  }

  request('POST', `/v1/account/campaigns/${props.providerId}/previews`, {}, { provider_values: { ...obj.value } })
    .then((res) => {
      previews.value = (res?.data as OrderPreview[]) || [];
    })
    .catch(() => {
      // keep whatever list we already have; the render below still uses the current key
    })
    .finally(() => {
      calcPreview();
    });
}, 300);

const previewDebouncer = debounce(() => {
  const seq = ++requestSeq;

  if (currentPreview.value && currentPreview.value.key) {
    // Spinner only for the very first load — on refetches the previous
    // iframe stays visible and we preload the next payload in the hidden one.
    if (!hasAnyPreview.value) loadingPreview.value = true;
    nextTick(() => {
      // a newer navigation started before this one even got to fetch — bail out
      if (seq !== requestSeq) return;

      const target: 'a' | 'b' = activeFrame.value === 'a' ? 'b' : 'a';
      // uses the preview's static configured height until the freshly loaded
      // iframe reports its real content height via onPreviewMessage
      applyFrameStyle(target);

      request(
        'POST',
        `/v1/account/campaigns/${props.providerId}/preview/${currentPreview.value!.key}`,
        {},
        { provider_values: { ...obj.value } }
      )
        .then((res) => {
          // a newer navigation was issued while this fetch was in flight — its response may
          // resolve out of order, so discard it rather than clobber the frame with stale content
          if (seq !== requestSeq) return;

          const newContent = res as string;
          // force a real navigation even if the content is byte-identical to what's already in
          // this frame slot — both iframes stay mounted permanently (only toggled via opacity),
          // so without a fresh navigation a previous "more" click's DOM mutation would survive
          // untouched while applyFrameStyle() above has already reset the frame to its collapsed size
          if (target === 'a') {
            iFrameSrcDocA.value = null;
          } else {
            iFrameSrcDocB.value = null;
          }
          nextTick(() => {
            if (seq !== requestSeq) return;

            pendingFrame.value = target;
            if (target === 'a') {
              iFrameSrcDocA.value = newContent;
            } else {
              iFrameSrcDocB.value = newContent;
            }
          });
          // activeFrame flips in onFrameLoad once the browser finishes painting the real content.
        })
        .finally(() => {
          if (seq === requestSeq) loadingPreview.value = false;
        });
    });
  } else {
    iFrameSrcDocA.value = null;
    iFrameSrcDocB.value = null;
    activeFrame.value = 'a';
    pendingFrame.value = null;
    loadingPreview.value = false;
  }
}, 300);

const getPreviewScale = (heightOverride?: number) => {
  if (previewContentRef.value) {
    const boundingClient = previewContentRef.value.getBoundingClientRect();
    const containerWidth = boundingClient.width;
    const containerHeight = boundingClient.height;

    const iframeWidth = currentPreview.value?.width;
    const iframeHeight = heightOverride ?? currentPreview.value?.height;

    let xScalingFactor = 1;
    let yScalingFactor = 1;

    if (iframeWidth && iframeHeight) {
      if (iframeWidth > containerWidth) {
        xScalingFactor = containerWidth / iframeWidth;
      }
      if (iframeHeight > containerHeight) {
        yScalingFactor = containerHeight / iframeHeight;
      }

      const scale = Math.min(xScalingFactor ?? 1, yScalingFactor ?? 1);

      return { width: iframeWidth, height: iframeHeight, scale, boundingClient };
    }
  }
  return { width: 0, height: 0, scale: 1 };
};

onMounted(() => {
  window.addEventListener('message', onPreviewMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onPreviewMessage);
});
</script>

<style scoped>
.preview-content {
  width: 100%;
  height: 100%;
  overflow: hidden;
  margin-top: 10px;
  margin-bottom: 10px;
  position: relative;
}

.preview-frame {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.preview-frame.active {
  opacity: 1;
  pointer-events: auto;
}

.border-top {
  border-top: 1px solid #e9ecef !important;
}
.no-preview-container {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
</style>
